import { useMemo, useState } from 'react';
import { AnalyticsInputs, AnalyticsPreset, AnalyticsSummary, getPresetRange, computeAnalyticsSummary } from '../services/analyticsService';
import { CanvasNode, HistoryItem, LightHistoryItem, RecentFileMetadata, MODEL_PRICING } from '../types';

const RATE_STORAGE_KEY = 'analyticsCostAssumptions';

const loadRateAssumptions = (): Record<string, number> => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = window.localStorage.getItem(RATE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.warn('Failed to load analytics rate overrides', err);
  }
  return {};
};

const saveRateAssumptions = (assumptions: Record<string, number>) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(RATE_STORAGE_KEY, JSON.stringify(assumptions));
};

const mergeWithPricing = (overrides: Record<string, number>) => ({
  ...MODEL_PRICING,
  ...overrides,
});

export const useAnalyticsData = ({
  nodes,
  history,
  recentFiles,
}: {
  nodes: CanvasNode[];
  history: (HistoryItem | LightHistoryItem)[];
  recentFiles: RecentFileMetadata[];
}) => {
  const [preset, setPreset] = useState<AnalyticsPreset>('1month');
  const [customRange, setCustomRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [rateOverrides, setRateOverrides] = useState<Record<string, number>>(mergeWithPricing(loadRateAssumptions()));

  const timeframe = useMemo(() => {
    if (preset === 'custom' && customRange.start && customRange.end) {
      return getPresetRange('custom', {
        start: new Date(customRange.start).getTime(),
        end: new Date(customRange.end).getTime(),
      });
    }
    return getPresetRange(preset);
  }, [preset, customRange]);

  const summary = useMemo<AnalyticsSummary>(() => {
    const inputs: AnalyticsInputs = {
      nodes,
      history,
      timeframe,
      rateAssumptions: rateOverrides,
      recentFiles,
    };
    return computeAnalyticsSummary(inputs);
  }, [nodes, history, timeframe, rateOverrides, recentFiles]);

  const updateRate = (model: string, value: number) => {
    const filteredValue = Number(value) || 0;
    const updated = { ...rateOverrides, [model]: filteredValue };
    setRateOverrides(updated);
    saveRateAssumptions(updated);
  };

  return {
    summary,
    preset,
    setPreset,
    customRange,
    setCustomRange,
    timeframe,
    rateAssumptions: rateOverrides,
    updateRate,
  };
};